// src/PlantTreeIdentification.js
import React, { useState, useEffect } from 'react';
import { db, storage, functions } from './firebase.js';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const PlantTreeIdentification = ({ user, isAdmin, isMobile }) => {
  const [plants, setPlants] = useState([]);
  const [editingPlant, setEditingPlant] = useState(null);
  const [flippedCard, setFlippedCard] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [status, setStatus] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Singular for filtering (matches API), but we'll map to plural for display
  const plantTypes = ['plant', 'vegetable', 'fruit', 'mushroom', 'tree', 'other'];
  const typeDisplayMap = {
    'plant': 'Plants',
    'vegetable': 'Vegetables',
    'fruit': 'Fruits',
    'mushroom': 'Mushrooms',
    'tree': 'Trees',
    'other': 'Other'
  };

  useEffect(() => {
    console.log('Setting up Firestore listener for flora collection');
    const unsubscribe = onSnapshot(collection(db, 'flora'), (snapshot) => {
      const fetchedPlants = snapshot.docs.map(doc => {
        const data = doc.data();
        const displayImageUrl = data.imageUrl || (data.images && data.images[0]) || 'undefined';
        console.log(`Plant ID: ${doc.id}, imageUrl: ${displayImageUrl}`);
        return { 
          id: doc.id, 
          ...data, 
          displayImageUrl,
          type: data.type ? data.type.toLowerCase().replace(/s$/, '') : 'other'  // Normalize: lowercase, remove plural 's'
        };
      });
      console.log('Fetched plants:', fetchedPlants);
      if (snapshot.empty) {
        console.log('No documents found in flora collection');
      }
      setPlants(fetchedPlants);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching flora:', error);
      setError(error.message);
      setLoading(false);
    });
    return () => {
      console.log('Cleaning up Firestore listener');
      unsubscribe();
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    console.log('Selected file:', file?.name || 'none');
    if (!file) return;
    setUploadError(null);
    setStatus('Uploading...');
    try {
      const storageRef = ref(storage, `flora_images/${Date.now()}_${file.name}`);
      console.log('Uploading file to:', storageRef.fullPath);
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Upload result:', uploadResult);
      const imageUrl = await getDownloadURL(storageRef);
      console.log('Generated imageUrl:', imageUrl);

      setStatus('Processing...');
      const identifyPlant = httpsCallable(functions, 'apiIdentifyPlant');
      const result = await identifyPlant({ imageUrl });
      console.log('Plant identification result:', result.data);

      let plantData = result.data;
      if (plantData.error || !plantData) {
        console.log('Plant unclassified or no data, creating default data');
        plantData = {
          type: 'other',
          common_name: 'Unclassified',
          scientific_name: 'Unknown',
          genus: 'Unknown',
          species: 'Unknown',
          habitat: 'Unknown',
          uses: 'Unknown',
          invasive: 'No',
          imageUrl,
          images: [imageUrl],
          created_by: user.uid
        };
      } else {
        plantData.invasive = plantData.invasive ? 'Yes' : 'No';
        plantData.imageUrl = imageUrl;
        plantData.images = [imageUrl];
        plantData.created_by = user.uid;
        plantData.type = plantData.type ? plantData.type.toLowerCase().replace(/s$/, '') : 'other';  // Normalize
      }

      setStatus('Creating card...');
      const docRef = await addDoc(collection(db, 'flora'), plantData);
      console.log('Added plant document with ID:', docRef.id, 'imageUrl:', imageUrl);
      setUploadError(null);
      setStatus(null);
    } catch (error) {
      console.error('Upload/Identification error:', error);
      setUploadError(error.message || 'Internal error');
      const storageRef = ref(storage, `flora_images/${Date.now()}_${file.name}`);
      console.log('Uploading fallback file to:', storageRef.fullPath);
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Fallback upload result:', uploadResult);
      const imageUrl = await getDownloadURL(storageRef);
      console.log('Generated fallback imageUrl:', imageUrl);
      setStatus('Creating fallback card...');
      const docRef = await addDoc(collection(db, 'flora'), {
        type: 'other',
        common_name: 'Unclassified',
        scientific_name: 'Unknown',
        genus: 'Unknown',
        species: 'Unknown',
        habitat: 'Unknown',
        uses: 'Unknown',
        invasive: 'No',
        imageUrl,
        images: [imageUrl],
        created_by: user.uid
      });
      console.log('Added fallback plant document with ID:', docRef.id, 'imageUrl:', imageUrl);
      setStatus(null);
    }
  };

  // Batch reclassify uncategorized plants
  const reclassifyUncategorized = async () => {
    setReclassifying(true);
    setStatus('Reclassifying plants... This may take a minute.');
    try {
      const uncategorized = plants.filter(p => p.type === 'other');
      console.log(`Reclassifying ${uncategorized.length} plants...`);

      for (const plant of uncategorized) {
        if (!plant.imageUrl) {
          console.warn(`Skipping plant ${plant.id}: No imageUrl`);
          continue;
        }

        const identifyPlant = httpsCallable(functions, 'apiIdentifyPlant');
        const result = await identifyPlant({ imageUrl: plant.imageUrl });
        console.log(`Re-identification for ${plant.id}:`, result.data);

        let updatedData = result.data;
        if (!updatedData.error && updatedData) {
          updatedData.type = updatedData.type ? updatedData.type.toLowerCase().replace(/s$/, '') : 'other';
          updatedData.invasive = updatedData.invasive ? 'Yes' : 'No';
          // Optionally update other fields if desired (e.g., habitat, uses)
        } else {
          console.warn(`Failed to reclassify ${plant.id}, keeping as 'other'`);
          continue;
        }

        await updateDoc(doc(db, 'flora', plant.id), updatedData);
        console.log(`Updated plant ${plant.id} with type: ${updatedData.type}`);
      }

      console.log('Reclassification complete');
      setStatus(null);
    } catch (error) {
      console.error('Reclassification error:', error);
      setStatus('Error during reclassification: ' + error.message);
    } finally {
      setReclassifying(false);
    }
  };

  const handleEdit = (plant) => {
    setEditingPlant(plant);
  };

  const handleSaveEdit = async () => {
    if (!editingPlant) return;
    try {
      await updateDoc(doc(db, 'flora', editingPlant.id), {
        type: editingPlant.type.toLowerCase().replace(/s$/, '') || 'other',  // Normalize
        common_name: editingPlant.common_name,
        scientific_name: editingPlant.scientific_name,
        genus: editingPlant.genus,
        species: editingPlant.species,
        habitat: editingPlant.habitat,
        uses: editingPlant.uses,
        invasive: editingPlant.invasive,
      });
      setEditingPlant(null);
    } catch (error) {
      console.error('Edit save error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this plant?')) {
      try {
        await deleteDoc(doc(db, 'flora', id));
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  const toggleFlip = (id) => {
    setFlippedCard(flippedCard === id ? null : id);
  };

  const handleImageError = (id, url) => {
    console.error(`Image load error for ${id}: ${url}`);
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      {uploadError && <p className="text-red-500">{uploadError}</p>}
      {status && <p className="text-blue-500">{status}</p>}
      {user && (
        <>
          <label className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center">
            Upload Image
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept="image/*"
            />
          </label>
          {isAdmin && (
            <button
              onClick={reclassifyUncategorized}
              disabled={reclassifying}
              className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center"
            >
              {reclassifying ? 'Reclassifying...' : 'Reclassify'}
            </button>
          )}
        </>
      )}
      <select
        value={selectedCategory}
        onChange={handleCategoryChange}
        className="block w-full max-w-[15rem] ml-0 mb-6 p-2 border border-gray-300 rounded"
      >
        <option value="All">All Categories</option>
        {plantTypes.map(type => (
          <option key={type} value={type}>
            {typeDisplayMap[type]}
          </option>
        ))}
      </select>

      {plants.length === 0 && (
        <p className="text-center text-gray-500">No plants found. Upload a new image to start.</p>
      )}

      {(selectedCategory === 'All' ? plantTypes : [selectedCategory]).map(type => (
        <div key={type}>
          <h3 className="text-xl font-semibold mt-6 mb-4">{typeDisplayMap[type]}</h3>
          <div className="flora-grid">
            {plants.filter(plant => plant.type === type).map(plant => (
              <div key={plant.id} className="card-container">
                <div className={`card ${flippedCard === plant.id ? 'flipped' : ''}`} onClick={() => toggleFlip(plant.id)}>
                  <div className="card-front">
                    <div className="card-image-area">
                      {imageErrors[plant.id] || !plant.displayImageUrl ? (
                        <img
                          src="https://placehold.co/400x320/e5e7eb/6b7280?text=No+Image&font=roboto"
                          alt="Unavailable"
                        />
                      ) : (
                        <img
                          src={plant.displayImageUrl}
                          alt={plant.common_name}
                          onError={() => handleImageError(plant.id, plant.displayImageUrl)}
                        />
                      )}
                    </div>
                    <div className="card-info">
                      <h3>{plant.common_name}</h3>
                      <p>Scientific: {plant.scientific_name}</p>
                    </div>
                    {user && (
                      <div className="card-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(plant); }}
                          className="admin-users-action-button"
                        >
                          Edit
                        </button>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(plant.id); }}
                            className="admin-users-delete-button"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="card-back bg-gray-300 text-left">
                    <p className="text-sm"><strong>Genus:</strong> {plant.genus}</p>
                    <p className="text-sm"><strong>Species:</strong> {plant.species}</p>
                    <p className="text-sm"><strong>Habitat:</strong> {plant.habitat}</p>
                    <p className="text-sm"><strong>Uses:</strong> {plant.uses}</p>
                    <p className="text-sm"><strong>Invasive:</strong> {plant.invasive}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editingPlant && (
        <div className={`modal-overlay ${isMobile ? 'modal-content-mobile' : ''}`}>
          <div className="modal-content">
            <h2 className="modal-title">Edit Plant</h2>
            <input
              type="text"
              value={editingPlant.type}
              onChange={e => setEditingPlant({ ...editingPlant, type: e.target.value })}
              placeholder="Type (e.g., tree)"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingPlant.common_name}
              onChange={e => setEditingPlant({ ...editingPlant, common_name: e.target.value })}
              placeholder="Common Name"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingPlant.scientific_name}
              onChange={e => setEditingPlant({ ...editingPlant, scientific_name: e.target.value })}
              placeholder="Scientific Name"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingPlant.genus}
              onChange={e => setEditingPlant({ ...editingPlant, genus: e.target.value })}
              placeholder="Genus"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingPlant.species}
              onChange={e => setEditingPlant({ ...editingPlant, species: e.target.value })}
              placeholder="Species"
              className="modal-input h-16"
            />
            <textarea
              value={editingPlant.habitat}
              onChange={e => setEditingPlant({ ...editingPlant, habitat: e.target.value })}
              placeholder="Habitat"
              className="modal-textarea h-24"
            />
            <textarea
              value={editingPlant.uses}
              onChange={e => setEditingPlant({ ...editingPlant, uses: e.target.value })}
              placeholder="Uses"
              className="modal-textarea h-24"
            />
            <input
              type="text"
              value={editingPlant.invasive}
              onChange={e => setEditingPlant({ ...editingPlant, invasive: e.target.value })}
              placeholder="Invasive (Yes/No)"
              className="modal-input h-16"
            />
            <div className="modal-button-group">
              <button
                onClick={handleSaveEdit}
                className="modal-button modal-button-primary"
              >
                Save
              </button>
              <button
                onClick={() => setEditingPlant(null)}
                className="modal-button modal-button-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlantTreeIdentification;